"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";

import SelectInput from "@/components/form/Select/Select";
import IncomeEligibilityNote from "@/components/form/IncomeEligibilityNote";
import { TextArea } from "@/components/form/TextArea";
import { TextInput } from "@/components/form/TextInput";
import { useEffect, useState, useMemo } from "react";

import { publicLeadCaptureFallback } from "@/config/leadCapturePublicFallback.js";
import If from "@/components/If";
import { Button } from "@/components/actions/Button";
import { Stack } from "@/components/structure/Stack";
import { handleContactForm } from "@/actions";
import PhoneInput from "@/components/form/TextInput/PhoneInput";
import { RadioGroup } from "@/components/form/RadioGroup";
import { useFieldErrors } from "@/components/form/useFieldErrors";
import Script from "next/script";
import { sendGAEvent } from "@next/third-parties/google";

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const initialState = {
  message: "",
};

type PublicLeadCaptureConfig = {
  interests: Array<{
    value: string;
    label: string;
    locations: Array<{ value: string; label: string }>;
  }>;
};

export default function Form() {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [interest, setInterest] = useState("");
  const [state, setState] = useState("idle");
  const { errors, validate, clearError } = useFieldErrors();
  const [howDidYouHear, setHowDidYouHear] = useState("");
  const [utmParams, setUtmParams] = useState<Record<string, string | null>>({});
  const [leadCaptureConfig, setLeadCaptureConfig] = useState<PublicLeadCaptureConfig>(
    publicLeadCaptureFallback,
  );

  // useEffect(() => {
  //   if (recaptchaLoaded) {
  //     window.grecaptcha.ready(() => {
  //       console.log("reCAPTCHA is ready");
  //     });
  //   }
  // }, [recaptchaLoaded]);

  // Capture UTM parameters from URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const utm = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content'),
      };
      setUtmParams(utm);
    }
  }, []);

  useEffect(() => {
    fetch("/api/lead-capture-config", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((config) => {
        if (config?.interests) setLeadCaptureConfig(config);
      })
      .catch(() => undefined);
  }, []);

  const locationOptions = useMemo(() => {
    const selectedInterest = leadCaptureConfig.interests.find(
      (entry: PublicLeadCaptureConfig["interests"][number]) =>
        entry.value === interest,
    );
    return selectedInterest?.locations || [];
  }, [interest, leadCaptureConfig]);

  if (!RECAPTCHA_SITE_KEY) {
    console.error("reCAPTCHA site key is missing");
    return null;
  }

  return (
    <>
      {/* <Script
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
        onLoad={() => setRecaptchaLoaded(true)}
        strategy="lazyOnload"
      /> */}
      <form
        noValidate
        onInput={clearError}
        onSubmit={async (ev) => {
          ev.preventDefault();
          setState("loading");

          // if (!recaptchaLoaded) {
          //   console.error("reCAPTCHA not loaded");
          //   return;
          // }

          // const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          //   action: "submit",
          // });
          const formData = new FormData(ev.target);
          const data = Object.fromEntries(formData) as Record<string, string>;
          // data.token = token;
          const requiredFields = [
            "firstName",
            "lastName",
            "email",
            "phone",
            "preferredContact",
            "interests",
            "location", // Location is now required
            "comments",
            // "token",
          ];
          if (
            !validate(ev.currentTarget as HTMLFormElement, data, requiredFields)
          ) {
            setState("idle");
            return;
          }
          const resp = await fetch("/api/contact", {
            method: "POST",
            body: JSON.stringify(data),
          }).catch((err: Error) => {
            console.error(err);
            setState("error");
            return null;
          });

          if (!resp) {
            return;
          }

          const json = await resp.json().catch((err: Error) => {
            console.error(err);
            setState("error");
            return null;
          });

          if (!json) {
            return;
          }

          if (!resp.ok) {
            setState("error");
            return;
          }

          if (json.error || json.missing) {
            setState("error");
            return;
          }

          if (json.success) {
            sendGAEvent("event", "contact_form_submission", {
              value: "success",
            });
            setState("success");
            return;
          }
          // /handleContactForm(ev);
        }}
        className=" w-[700px] max-w-full mx-auto relative z-[2] px-gutter"
      >
        <Stack gap="tight">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput label="First Name" name="firstName" error={errors.firstName} required />
            <TextInput label="Last Name" name="lastName" error={errors.lastName} required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhoneInput label="Phone Number" name="phone" error={errors.phone} required />
            <TextInput label="Email" name="email" error={errors.email} required type="email" />
          </div>

          <RadioGroup
            legend="What is your preferred method of contact?"
            name="preferredContact"
            error={errors.preferredContact}
            testId="lead-preferred-contact"
            options={[
              { value: "Phone", label: "Phone" },
              { value: "Email", label: "Email" },
            ]}
            required
          />

          <SelectInput
            placeholder="I'm interested in..."
            options={leadCaptureConfig.interests}
            name="interests"
            error={errors.interests}
            testId="lead-interest"
            value={interest}
            onChange={(value) => setInterest(value)}
          />

          <If condition={interest === "Affordable Housing"}>
            <IncomeEligibilityNote />
          </If>

          <SelectInput
            placeholder="Location"
            options={locationOptions}
            name="location"
            error={errors.location}
            testId="lead-location"
            required
          />

          {/* Hidden inputs for UTM parameters */}
          {Object.entries(utmParams).map(([key, value]) =>
            value && (
              <input
                key={key}
                type="hidden"
                name={key}
                value={value}
              />
            )
          )}

          {/* <TextInput label="How did you hear about us?" name="howDidYouHear" /> */}
          <SelectInput
            placeholder="How did you hear about us?"
            options={[
              { value: "Online Search", label: "Online Search" },
              { value: "Direct Mail", label: "Direct Mail" },
              { value: "Print Ad", label: "Print Ad" },
              { value: "Radio", label: "Radio" },
              {
                value: "Referral (Resident/Family/Friend)",
                label: "Referral (Resident/Family/Friend)",
              },
              {
                value: "Referral (Professional/Healthcare Provider)",
                label: "Referral (Professional/Healthcare Provider)",
              },
              { value: "Social Media", label: "Social Media" },
              { value: "Television", label: "Television" },
              { value: "Other", label: "Other" },
            ]}
            name="howDidYouHear"
            value={howDidYouHear}
            onChange={(value) => setHowDidYouHear(value)}
          />
          <If condition={howDidYouHear === "Other"}>
            <TextInput
              label="Other (Please Specify)"
              name="howDidYouHearOther"
            />
          </If>
          <TextArea
            label="Message"
            name="comments"
            error={errors.comments}
            placeholder="Enter your message here"
            required
          />

          <label htmlFor="remark-reservation" className="remark">
            Remark
          </label>
          <input
            className="remark"
            name="remark"
            id="remark-reservation"
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{(2, 4)}$"
            placeholder="name@domain.com"
          />

          <If condition={state === "error"}>
            <p className="text-red-500">
              There was an error submitting the form
            </p>
          </If>

          <If condition={state === "success"}>
            <p data-testid="lead-submit-success" className="text-green-500 text-center">
              Form submitted successfully
            </p>
            <button
              onClick={() => setState("idle")}
              className="px-0 text-center text-orange underline"
            >
              Send Another Contact Form
            </button>
          </If>

          {/* <p aria-live="polite">{state?.message}</p> */}
          <If condition={state !== "success"}>
            <Button
              type="submit"
              className="w-max"
              disabled={state === "loading"}
              color="outlineDark"
            >
              <If condition={state === "loading"}>Sending...</If>
              <If condition={state !== "loading"}>Send Request</If>
            </Button>
          </If>

          {/* <small className="text-[#929292] text-sm">
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              className="text-[#929292] underline text-sm"
              href="https://policies.google.com/privacy"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              className="text-[#929292] underline text-sm"
              href="https://policies.google.com/terms"
            >
              Terms of Service
            </a>{" "}
            apply.
          </small> */}
        </Stack>
      </form>
    </>
  );
}
